import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Rating,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { Star, CheckCircle } from '@mui/icons-material';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const RateServicePage: React.FC = () => {
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [serviceInfo, setServiceInfo] = useState<any>(null);
  const [alreadyRated, setAlreadyRated] = useState(false);

  useEffect(() => {
    if (serviceRequestId) {
      fetchServiceInfo();
      checkExistingReview();
    }
  }, [serviceRequestId]);

  const fetchServiceInfo = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/service-requests/${serviceRequestId}`);
      
      if (response.success && response.data) {
        const serviceData = response.data as any;
        setServiceInfo(serviceData);
        
        // Verify service is completed
        if (serviceData.status !== 'completed') {
          setError('You can only rate completed services.');
        }
      } else {
        setError('Service request not found.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load service information.');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingReview = async () => {
    try {
      const response = await apiService.get(`/reviews/service-request/${serviceRequestId}`);
      
      if (response.success && response.data) {
        const reviewData = response.data as any;
        if (reviewData.review) {
          setAlreadyRated(true);
          setRating(reviewData.review.rating);
          setComment(reviewData.review.comment || '');
          setTitle(reviewData.review.title || '');
        }
      }
    } catch (err: any) {
      // Review doesn't exist yet, that's fine
      console.log('No existing review found');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!rating) {
      setError('Please select a rating (1-5 stars).');
      return;
    }

    if (!serviceRequestId) {
      setError('Invalid service request ID.');
      return;
    }

    // If not logged in, redirect to login
    if (!user || user.userType !== 'customer') {
      navigate(`/login?redirect=/rate/${serviceRequestId}`);
      return;
    }

    // Verify customer owns this service
    const customerId = serviceInfo?.customerId?._id?.toString() || serviceInfo?.customerId?.toString() || serviceInfo?.customerId;
    const userId = user?._id?.toString() || user?.id?.toString() || user?.id;
    if (serviceInfo && customerId && userId && customerId !== userId) {
      setError('You can only rate services you have requested.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const reviewData = {
        serviceRequestId,
        rating,
        comment: comment || `Customer rating via web (${rating} out of 5)`,
        title: title || `${rating}-Star Rating`,
      };

      const response = await apiService.post('/reviews', reviewData);

      if (response.success) {
        setSuccess(true);
        // Redirect after 2 seconds
        setTimeout(() => {
          navigate('/customer');
        }, 2000);
      } else {
        setError(response.error || 'Failed to submit review.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit review.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !serviceInfo) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
            Go to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        {success ? (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Thank You!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your review has been submitted successfully.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Redirecting to your dashboard...
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="h4" gutterBottom align="center">
              Rate Your Service
            </Typography>
            
            {serviceInfo && (
              <Card sx={{ mb: 3, mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Service: {serviceInfo.serviceType?.name || 'Lawn Care Service'}
                  </Typography>
                  {serviceInfo.address && (
                    <Typography variant="body2" color="text.secondary">
                      {serviceInfo.address.street}, {serviceInfo.address.city}, {serviceInfo.address.state}
                    </Typography>
                  )}
                  {serviceInfo.completedDate && (
                    <Typography variant="body2" color="text.secondary">
                      Completed: {new Date(serviceInfo.completedDate).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {alreadyRated && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You've already rated this service. You can update your rating below.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {(!user || user.userType !== 'customer') && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please log in to submit your review.
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  How would you rate this service? *
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                  <Rating
                    name="rating"
                    value={rating}
                    onChange={(event, newValue) => {
                      setRating(newValue);
                      setError('');
                    }}
                    size="large"
                    icon={<Star sx={{ fontSize: 40 }} />}
                    emptyIcon={<Star sx={{ fontSize: 40, color: '#e0e0e0' }} />}
                  />
                  {rating && (
                    <Typography variant="h6" color="text.secondary">
                      {rating} / 5
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  1 = Poor, 5 = Excellent
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Title (Optional)"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                margin="normal"
                placeholder="e.g., Great service!"
              />

              <TextField
                fullWidth
                label="Comments (Optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                margin="normal"
                multiline
                rows={4}
                placeholder="Tell us about your experience..."
              />

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!rating || submitting || (!user || user.userType !== 'customer')}
                  size="large"
                >
                  {submitting ? <CircularProgress size={24} /> : alreadyRated ? 'Update Review' : 'Submit Review'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/customer')}
                  disabled={submitting}
                  size="large"
                >
                  Cancel
                </Button>
              </Box>
            </form>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default RateServicePage;

